import { useState } from 'react';
import type { ObservacionItem } from '@lib/services/observaciones.service';

interface Props {
  item: ObservacionItem;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (confirmDocumento: string) => void;
}

/**
 * Modal destructivo type-to-confirm para eliminar a un cónyuge/hijo/adherente
 * de un legajo. El admin tiene que escribir el documento del integrante
 * exacto para habilitar el botón, mismo patrón que EliminarPadronModal.
 */
export function EliminarFamiliarModal({ item, submitting, error, onClose, onConfirm }: Props) {
  const [typed, setTyped] = useState('');
  const docEsperado = (item.asociadoDocumento ?? '').trim();
  const valid = typed.trim() === docEsperado && docEsperado.length > 0;

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <header style={{ marginBottom: 16 }}>
          <h2 className="heading-3" style={{ color: 'var(--color-error-dark)' }}>
            Eliminar integrante
          </h2>
          <p className="text-element-mid" style={{ marginTop: 6, fontSize: 14 }}>
            Esta acción <strong>no se puede deshacer</strong>. Se van a borrar:
          </p>
          <ul
            className="text-element-mid"
            style={{ marginTop: 8, marginLeft: 20, fontSize: 13, lineHeight: 1.6 }}
          >
            <li>
              El integrante <strong>{item.asociadoNombre || '(sin nombre)'}</strong>{' '}
              <span className="status status-info" style={{ marginLeft: 4 }}>
                {item.asociadoParentesco || '?'}
              </span>{' '}
              del legajo <strong>{item.legajoNumero}</strong>
            </li>
            <li>Su declaración jurada de salud (si la tenía)</li>
            <li>Las observaciones pendientes sobre este integrante (se marcan resueltas)</li>
          </ul>
        </header>

        <label className="formField" style={{ display: 'block', marginTop: 12 }}>
          <span style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Para confirmar, escribí exactamente el documento del integrante:
          </span>
          <code
            style={{
              display: 'block',
              padding: '6px 10px',
              background: 'var(--color-surface-accent)',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 8,
              wordBreak: 'break-all',
            }}
          >
            {docEsperado || '(este integrante no tiene documento cargado)'}
          </code>
          <input
            className="input"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/\D/g, ''))}
            inputMode="numeric"
            autoFocus
            disabled={!docEsperado || submitting}
          />
        </label>

        {error && (
          <p className="status status-error" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={{ background: 'var(--color-error-dark)' }}
            onClick={() => onConfirm(typed.trim())}
            disabled={!valid || submitting}
          >
            {submitting ? 'Eliminando…' : 'Eliminar integrante'}
          </button>
        </footer>
      </div>
    </div>
  );
}
